import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanLoad,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
} from '@angular/router';
import { AuthService } from '../services/auth.service';
import { FeatureType } from '../shared/menu-items/menu-item-shared';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate, CanLoad {
  constructor(private authService: AuthService, private router: Router) {}

  /**
   * Returns a boolean indicating whether or not navigation to a route should be allowed.
   * If the user is not authenticated, they are re-routed to login.
   * @param next
   * @param state
   */
  public canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean> {
    return this.authService
      .isAuthenticated()
      .then((isAuthenticated) => {
        if (isAuthenticated === false) {
          this.navigateToLogin();
          return false;
        }

        const requiredFeatures = next.data.requiredFeatures;

        if (requiredFeatures) {
          let result = this.authService.hasFeatures(requiredFeatures);

          // If the user want's to navigate to one of these features (Reports / Maintenance / Price Plans /
          // Card terminals) and has no permission, he should be redirected to the license information page.
          if (
            result === false &&
            (requiredFeatures.includes(FeatureType.Reports) ||
              requiredFeatures.includes(FeatureType.StationMaintenance) ||
              requiredFeatures.includes(FeatureType.PricePlans) ||
              requiredFeatures.includes(FeatureType.CardTerminals))
          ) {
            // license information
            const param = state.url.slice(1).replace('/', '-');
            this.router.navigate(['/license-information/' + param]);
          }

          return result;
        }

        return true;
      })
      .catch((_) => {
        return false;
      });
  }

  /**
   * Returns a boolean indicating whether or not children can be loaded
   */
  public canLoad(route: Route, segments: UrlSegment[]): Promise<boolean> {
    const requiredFeatures = route.data.requiredFeatures;

    if (requiredFeatures) {
      const features = this.authService.hasFeatures(requiredFeatures);

      // If the user want's to navigate to one of these features (Reports / Maintenance / Price Plans /
      // Card terminals) and has no permission, he should be redirected to the license information page.
      if (
        features === false &&
        (requiredFeatures.includes(FeatureType.Reports) ||
          requiredFeatures.includes(FeatureType.StationMaintenance) ||
          requiredFeatures.includes(FeatureType.PricePlans) ||
          requiredFeatures.includes(FeatureType.CardTerminals))
      ) {
        // license information
        const param = segments.join('-');
        this.router.navigate(['/license-information/' + param]);
      }

      return Promise.resolve(features);
    }

    return Promise.resolve(true);
  }

  private navigateToLogin(): void {
    this.router.navigate(['/authentication/login']);
  }
}
